import { useCallback, useState } from 'react';

import { useRouter } from 'expo-router';

import {
  ILoyaltyServiceNotificationQuery,
  useLoyaltyServiceMarkNotificationsAsReadMutation,
  useLoyaltyServiceNotificationLazyQuery,
  useLoyaltyUserNotificationsLazyQuery,
  useMarkLoyaltyNotificationsAsReadMutation,
} from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

import {
  ChallengeCompletedModalData,
  IUseLoyaltyUserNotifications,
  LoyaltyUserNotifications,
} from './types';

type LegacyNotification = NonNullable<LoyaltyUserNotifications>[number];
type LoyaltyServiceNotificationItem = NonNullable<
  ILoyaltyServiceNotificationQuery['loyaltyServiceNotification']
>['items'][number];

// loyalty-service notifications mirror the legacy shape 1:1, so downstream consumers
// (e.g. use-loyalty-celebrations.tsx) can keep matching against the legacy fields.
const toLegacyNotification = (
  notification: LoyaltyServiceNotificationItem
): LegacyNotification => ({
  __typename: 'LoyaltyNotification',
  id: notification.id,
  notificationType: notification.notificationType,
  entityId: notification.entityId ?? null,
  incentiveType: (notification.incentiveType as unknown) as LegacyNotification['incentiveType'],
  readAt: notification.readAt ?? null,
  wasRead: notification.wasRead,
  metadata: notification.metadata ?? null,
});

/**
 * Custom hook that manages the lifecycle of loyalty notifications for a user.
 *
 * It handles:
 * - Fetching user notifications from the GraphQL API
 * - Marking notifications as read
 * - Refetching the notification list when needed
 *
 */
export const useLoyaltyUserNotifications = ({
  loyaltyId,
}: {
  loyaltyId?: string;
}): IUseLoyaltyUserNotifications => {
  const { navigate } = useRouter();
  const enableLoyaltyServiceNotification = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_NOTIFICATION)
  );
  const [challengeCompletedModalData, setChallengeCompletedModalData] = useState<
    ChallengeCompletedModalData
  >({ monthlyChallengePoints: 0, profileChallengePoints: 0 });
  const [loyaltyNotifications, setLoyaltyNotifications] = useState<LoyaltyUserNotifications>([]);

  const [
    loyaltyUserNotificationsQuery,
    { loading: loadingNotifications },
  ] = useLoyaltyUserNotificationsLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: data => {
      setLoyaltyNotifications(data?.loyaltyUserV2?.notifications || []);
    },
  });

  const [
    loyaltyServiceNotificationQuery,
    { loading: loadingNotificationsWithLoyaltyService },
  ] = useLoyaltyServiceNotificationLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: data => {
      setLoyaltyNotifications(
        data?.loyaltyServiceNotification?.items.map(toLegacyNotification) ?? []
      );
    },
  });

  const [
    markNotificationsAsRead,
    { loading: markAsReadLoading },
  ] = useMarkLoyaltyNotificationsAsReadMutation();

  const [
    markNotificationsAsReadWithLoyaltyService,
    { loading: markAsReadLoadingWithLoyaltyService },
  ] = useLoyaltyServiceMarkNotificationsAsReadMutation();

  const fetchLoyaltyNotifications = useCallback(async () => {
    if (!loyaltyId) {
      return;
    }

    if (enableLoyaltyServiceNotification) {
      await loyaltyServiceNotificationQuery({ variables: { wasRead: false } });
      return;
    }

    await loyaltyUserNotificationsQuery({
      variables: { loyaltyId, where: { wasRead: false } },
    });
  }, [
    enableLoyaltyServiceNotification,
    loyaltyId,
    loyaltyServiceNotificationQuery,
    loyaltyUserNotificationsQuery,
  ]);

  const markLoyaltyNotificationsAsRead = useCallback(
    async (ids: string[]) => {
      if (!ids.length) {
        return;
      }

      try {
        if (enableLoyaltyServiceNotification) {
          await markNotificationsAsReadWithLoyaltyService({ variables: { input: { ids } } });
        } else {
          await markNotificationsAsRead({ variables: { input: { ids } } });
        }
      } catch (error) {
        logger.warn({
          error,
          message: `Failed to mark notifications as read`,
        });
      } finally {
        await fetchLoyaltyNotifications();
      }
    },
    [
      enableLoyaltyServiceNotification,
      fetchLoyaltyNotifications,
      markNotificationsAsRead,
      markNotificationsAsReadWithLoyaltyService,
    ]
  );

  const openChallengeCompletedModal = useCallback(
    ({ monthlyChallengePoints = 0, profileChallengePoints = 0 }: ChallengeCompletedModalData) => {
      if (monthlyChallengePoints === 0 && profileChallengePoints === 0) {
        return;
      }

      setChallengeCompletedModalData({
        monthlyChallengePoints,
        profileChallengePoints,
      });

      navigate(routes.loyaltyChallengeCompleted);
    },
    [navigate]
  );

  return {
    loyaltyNotifications,
    loadingNotifications: enableLoyaltyServiceNotification
      ? loadingNotificationsWithLoyaltyService
      : loadingNotifications,
    markAsReadLoading: enableLoyaltyServiceNotification
      ? markAsReadLoadingWithLoyaltyService
      : markAsReadLoading,
    challengeCompletedModalData,
    markLoyaltyNotificationsAsRead,
    fetchLoyaltyNotifications,
    openChallengeCompletedModal,
  };
};
